import React from 'react';
import { Link } from 'react-router-dom';
import { useStreak } from '../contexts/StreakContext';
import { useGlobalModal } from '../contexts/ModalContext';
import { hasCompletedGameToday, getCurrentDate, buildCurrentWeekDataFromDates, getCurrentWeekCompletion } from '../util';
import { useRecommendedGame } from '../hooks/useRecommendedGame';
import StreakIcons from './StreakIcons';
import styles from '../styles/StreakInfoModal.module.css';
import modalStyles from '../styles/modal.module.css';
import { GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES } from '../ads/googleInterstitialAttributes';

const StreakInfoModal = () => {
  const { streakData, isLoading } = useStreak();
  const { closeModal } = useGlobalModal();
  const currentDate = getCurrentDate();
  const { recommendation, isLoading: isLoadingRecommendation } = useRecommendedGame(currentDate, null, {
    subscribeOnly: true,
    allowBackgroundRevalidateOnSubscribe: true,
    priority: 'low',
  });

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <p>Caricamento...</p>
        </div>
      </div>
    );
  }

  const currentStreak = streakData?.currentStreak || 0;
  const longestStreak = streakData?.longestStreak || 0;
  // Prefer backend-provided completion data; fallback to local util
  const todayStr = getCurrentDate();
  const hasCompletedToday = Array.isArray(streakData?.completedDatesCurrentWeek)
    ? streakData.completedDatesCurrentWeek.includes(todayStr)
    : hasCompletedGameToday();

  const renderActionIndicator = (isCompleted, hasLink) => {
    if (isCompleted) {
      return (
        <div className={styles.completedIcon} aria-label="Completato">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 8L6.5 11.5L13 5" stroke="#ff6b35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      );
    }

    if (hasLink) {
      return (
        <div className={styles.actionButton} aria-label="Vai">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 3L11 8L6 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      );
    }

    return null;
  };

  const weekData = buildCurrentWeekDataFromDates(streakData?.completedDatesCurrentWeek) || getCurrentWeekCompletion();

  return (
    <div className={modalStyles.modalContent}>
      <div className={styles.header}>
        <div className={styles.streakDisplay}>
          <span className={styles.streakNumber}>{currentStreak}</span>
          <span className={styles.fireIcon}>🔥</span>
        </div>
        <div className={styles.weekDisplay}>
          <StreakIcons 
            weekData={weekData}
            showLabels={true}
          />
        </div>
      </div>
      <div className={styles.content}>
        <p className={styles.description}>
          Completa almeno una partita al giorno per mantenere accesa la tua fiamma.
        </p>
        
        <ul className={styles.statsList}>
          <li className={styles.statItem}>
            <span className={styles.statLabel}>Serie in corso</span>
            <span className={styles.statValue}>
              {currentStreak} {currentStreak === 1 ? 'giorno' : 'giorni'}
            </span>
          </li>
          <li className={styles.statItem}>
            <span className={styles.statLabel}>Serie più lunga</span>
            <span className={styles.statValue}>
              {longestStreak} {longestStreak === 1 ? 'giorno' : 'giorni'}
            </span>
          </li>
          {hasCompletedToday ? (
            <li className={styles.statItem}>
              <span className={styles.statLabel}>Serie estesa oggi</span>
              {renderActionIndicator(true, false)}
            </li>
          ) : recommendation ? (
            <li className={`${styles.statItem} ${styles.clickableRow}`}>
              <Link
                to={recommendation.path}
                className={styles.rowLink}
                onClick={closeModal}
                {...GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES}
              >
                <span className={styles.statLabel}>Estendi la tua serie</span>
                {renderActionIndicator(false, true)}
              </Link>
            </li>
          ) : (
            <li className={styles.statItem}>
              <span className={styles.statLabel}>Estendi la tua serie</span>
            </li>
          )}
        </ul>
        
        <div className={styles.learnMoreLink}>
          <Link to="/serie" onClick={closeModal} {...GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES}>
            Scopri di più sulla serie →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default StreakInfoModal;
